/**
 * AppAmbient — shared animated background for every authenticated page.
 *
 * Pure CSS animated aurora + grid + orb + scanner layers plus a tiny JS
 * scroll-parallax ride on the orb.  rAF-throttled, transform-only, GPU-friendly.
 * Respects `prefers-reduced-motion` (CSS handles that).
 */
import { useEffect, useRef } from "react";

export default function AppAmbient() {
  const orbRef = useRef(null);
  const gridRef = useRef(null);

  useEffect(() => {
    // Deterministic first-paint transform so parallax starts from a known state.
    if (orbRef.current) orbRef.current.style.transform = "translateX(-50%) translateY(0)";
    if (gridRef.current) gridRef.current.style.transform = "translateY(0)";
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    let rafId = 0;
    let pending = false;
    const onScroll = () => {
      if (pending) return;
      pending = true;
      rafId = requestAnimationFrame(() => {
        const y = window.scrollY || 0;
        if (orbRef.current) {
          orbRef.current.style.transform = `translateX(-50%) translateY(${(-y * 0.08).toFixed(1)}px)`;
        }
        if (gridRef.current) {
          gridRef.current.style.transform = `translateY(${(y * 0.04).toFixed(1)}px)`;
        }
        pending = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <>
      <div aria-hidden className="cs-app-ambient cs-app-ambient-aurora" />
      <div aria-hidden ref={orbRef}  className="cs-app-ambient cs-app-ambient-orb" />
      <div aria-hidden ref={gridRef} className="cs-app-ambient cs-app-ambient-grid" />
      <div aria-hidden className="cs-app-ambient cs-app-ambient-scanner" />
    </>
  );
}
